import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, Modal, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import { connect, useDispatch } from "react-redux";
import api, {
  firstLoginPromoFallback,
  standardSixMonthCheckoutProgramId,
} from "../constants/config";
import SnackBar from "../atoms/snackBar";
import { setUserData } from "../redux/action/user";
import { isSubscriptionActive } from "../common";

const DEFAULT_ENDPOINTS = {
  getOffer: "me/first-login-offer",
  dismissOffer: "me/first-login-offer/dismiss",
  createPayCheckout: "create-pay-checkout",
};

/** Backend may send shouldShow as boolean, 1/0, or string. */
function isAffirmative(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function pickPromoFromPayload(data) {
  if (!data || typeof data !== "object") return { code: "", label: "" };
  const code = (
    data.promoCode ??
    data.code ??
    data.couponCode ??
    data.promotionCode ??
    data.stripePromotionCode ??
    ""
  )
    .toString()
    .trim();
  const label = (data.discountLabel ?? data.label ?? "").toString().trim();
  return { code, label };
}

function pickShouldShowField(data) {
  if (!data || typeof data !== "object") return undefined;
  return (
    data.shouldShow ??
    data.showOffer ??
    data.show ??
    data.should_show ??
    data.should_display ??
    data.display ??
    undefined
  );
}

function messageFromApiError(err) {
  if (err == null) return null;
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    if (typeof err.message === "string" && err.message) return err.message;
    if (typeof err.error === "string" && err.error) return err.error;
    if (err.error && typeof err.error.message === "string") return err.error.message;
    if (typeof err.msg === "string" && err.msg) return err.msg;
  }
  return null;
}

const FirstLoginUpgradeModal = (props) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [discountLabel, setDiscountLabel] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const token = localStorage.getItem("userKey");
  const isSubscribed = isSubscriptionActive(props?.user);

  const endpoints = useMemo(() => DEFAULT_ENDPOINTS, []);

  // Once opened, keep open until user action (dismiss/upgrade).
  // This avoids dev StrictMode double-effect or any re-fetch from auto-closing.
  const holdOpenUntilUserActionRef = useRef(false);

  const safeClose = () => {
    holdOpenUntilUserActionRef.current = false;
    setOpen(false);
    dispatch(
      setUserData({
        isLoggedinModal: false,
      })
    );
  };

  const fetchOffer = async () => {
    if (!token || isSubscribed) return;

    setIsLoading(true);
    if (!holdOpenUntilUserActionRef.current) {
      setPromoCode("");
      setDiscountLabel("");
    }
    try {
      const res = await api.get(endpoints.getOffer);
      const data = res?.data?.data ?? res?.data ?? {};

      let { code, label } = pickPromoFromPayload(data);
      if (!code && firstLoginPromoFallback) code = firstLoginPromoFallback;
      const showField = pickShouldShowField(data);
      const explicitlyHidden =
        showField === false ||
        showField === 0 ||
        showField === "0" ||
        showField === "false";
      const shouldShow = !explicitlyHidden && isAffirmative(showField);

      // If we've already opened, keep it open until user action.
      if (holdOpenUntilUserActionRef.current) {
        setOpen(true);
        if (code) setPromoCode(code);
        if (label) setDiscountLabel(label);
        return;
      }

      if (shouldShow) {
        holdOpenUntilUserActionRef.current = true;
        setOpen(true);
        if (code) setPromoCode(code);
        if (label) setDiscountLabel(label);
      } else if (!holdOpenUntilUserActionRef.current) {
        setOpen(false);
        dispatch(setUserData({ isLoggedinModal: false }));
      }
    } catch (e) {
      if (!holdOpenUntilUserActionRef.current) {
        setOpen(false);
        dispatch(setUserData({ isLoggedinModal: false }));
      }
      const msg = messageFromApiError(e) || "Could not load first-login offer.";
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isSubscribed) {
      setOpen(false);
      dispatch(setUserData({ isLoggedinModal: false }));
    }
  }, [isSubscribed, dispatch]);

  useEffect(() => {
    if (!token || isSubscribed) return;
    fetchOffer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isSubscribed]);

  const copyCode = async () => {
    if (!promoCode) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(promoCode);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = promoCode;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setSnack({ open: true, message: "Coupon code copied.", severity: "success" });
    } catch {
      setSnack({ open: true, message: "Failed to copy coupon code.", severity: "error" });
    }
  };

  const dismissOffer = async () => {
    try {
      await api.post(endpoints.dismissOffer);
    } catch {
      // Ignore; FE will still close.
    } finally {
      safeClose();
    }
  };

  const upgradeToStandard6mo = async () => {
    setCheckoutLoading(true);
    try {
      const couponFromModal = promoCode && String(promoCode).trim();
      const couponFromStorage = localStorage.getItem("couponCode");
      const coponCode =
        couponFromModal || (couponFromStorage && couponFromStorage.trim()) || null;

      const payload = {
        ID: standardSixMonthCheckoutProgramId,
        coponCode,
      };

      const res = await api.post(endpoints.createPayCheckout, payload);
      const url =
        res?.data?.data?.url ??
        res?.data?.url ??
        res?.data?.data?.sessionUrl ??
        res?.data?.sessionUrl;
      if (url) {
        safeClose();
        window.location.href = url;
      } else {
        setSnack({
          open: true,
          message: "Checkout URL missing from server response.",
          severity: "error",
        });
      }
    } catch (err) {
      const msg =
        messageFromApiError(err) ||
        "Could not start checkout. Check program ID and create-pay-checkout on the server.";
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <Fragment>
      <Modal open={open} onClose={dismissOffer} sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}>
        <Box sx={modalStyle} width={"95%"} maxWidth={"620px"} minWidth={"320px"}>
          <Box className="close-pop-wrap">
            <Box className="Heading">
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "#fff" }}>
              A Little Gift for You 😊 
              </Typography>
            </Box>
          </Box>

          <Box className="ContentWrapper">
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
              Commit to Transformation.
            </Typography>
            <Typography sx={{ mb: 2, fontWeight: 600 }}>
              Your life can completely change in 6 months.
            </Typography>

            <Typography sx={{ mb: 1.5 }}>
              Save 50% and get full access now for just{" "}
              <strong>$33/month</strong> for 6 months.
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ mb: 0.75 }}>✓ Your complete personalized action plan (50+ recommendations)</Typography>
              <Typography sx={{ mb: 0.75 }}>✓ Hundreds of hours of expert education</Typography>
              <Typography sx={{ mb: 0.75 }}>✓ Weekly doctor office hours</Typography>
              <Typography sx={{ mb: 0.75 }}>✓ Lab discounts + full eBook library</Typography>
            </Box>

            <Typography sx={{ mb: 2, fontWeight: 700 }}>
              This discounted offer disappears when you leave this screen. Sign up now and lock in this rate for life!
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                background: "#f4f4f4",
                borderRadius: "10px",
                padding: "10px 12px",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "monospace",
                  fontWeight: 800,
                  letterSpacing: "1px",
                  flex: 1,
                  userSelect: "all",
                  wordBreak: "break-all",
                }}
              >
                {promoCode || (isLoading ? "Loading..." : "")}
              </Typography>
              <IconButton
                aria-label="Copy coupon code"
                onClick={copyCode}
                disabled={!promoCode}
                size="small"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography sx={{ mt: 1.25, fontSize: "0.9rem", opacity: 0.85 }}>
              Apply this coupon code at checkout{discountLabel ? ` (${discountLabel})` : ""}.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mt: 3 }}>
              <Button
                variant="contained"
                onClick={upgradeToStandard6mo}
                sx={{
                  fontWeight: "bold",
                  background: "#E29C2E",
                  "&:hover": { background: "#C07D1A" },
                }}
                disabled={isLoading || checkoutLoading}
              >
                {checkoutLoading ? "Opening checkout…" : "Sign me up, I'm Committed →"}
              </Button>
              <Button
                variant="text"
                color="secondary"
                onClick={dismissOffer}
                sx={{ textTransform: "none" }}
              >
                No thanks, I'll stay on the free plan and pay full price later
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        handleCloseSnackBar={() => setSnack({ open: false, message: "", severity: "success" })}
      />
    </Fragment>
  );
};

export default connect(({ user }) => ({ user }))(FirstLoginUpgradeModal);

const modalStyle = {
  overflow: "hidden",
  position: "relative",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90vh",
  backgroundColor: "white",
  boxShadow: 24,
  pb: 2,
  borderRadius: "8px",
  ".close-pop-wrap": {
    top: "0px",
    right: "0px",
    position: "sticky",
    padding: "10px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#112225",
  },
  ".Heading": {
    display: "flex",
    alignItems: "center",
    h2: { mb: 0, color: "#fff" },
  },
  ".CloseImg": {
    opacity: 1,
    borderRadius: "50%",
    p: "2px",
    border: "1px solid #fff",
    fontSize: "0.7rem",
    cursor: "pointer",
    path: { fill: "#fff" },
  },
  ".ContentWrapper": {
    p: 2,
  },
};

